/**
 * Top-level error boundary (CLAUDE.md 16.6).
 *
 * Without one, a render error anywhere unmounts the whole tree and the engineer gets
 * a white page -- with unsaved inputs still in localStorage but no way to reach them.
 * This keeps the chrome, says what happened, and offers a reload.
 *
 * The stack is shown only in development. In production it goes to the console for a
 * support ticket, never onto the screen.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack)
  }

  reset = (): void => this.setState({ error: null })

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)
    return <DefaultFallback error={error} onReset={this.reset} />
  }
}

function DefaultFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-6 text-text">
      <div className="max-w-md space-y-3 text-center">
        <h1 className="text-lg font-semibold">Something went wrong</h1>
        <p className="text-sm text-text-muted">
          The page could not be displayed. Your unsaved inputs are kept in this browser.
        </p>
        {import.meta.env.DEV && (
          <pre className="scroll-x rounded border border-border bg-surface p-3 text-start text-xs">
            {error.message}
          </pre>
        )}
        <button
          type="button"
          onClick={onReset}
          className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-accent-fg"
        >
          Try again
        </button>
      </div>
    </div>
  )
}
